/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */
/*  Utf8 class: encode / decode between multi-byte Unicode characters and UTF-8 multiple          */
/*              single-byte character encoding (c) Chris Veness 2002-2012                         */
/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

/**
 * Encode multi-byte Unicode string into utf-8 multiple single-byte characters
 * (BMP / basic multilingual plane only)
 *
 * Chars in range U+0080 - U+07FF are encoded in 2 chars, U+0800 - U+FFFF in 3 chars
 *
 * @param {String} strUni Unicode string to be encoded as UTF-8
 * @returns {String} encoded string
 */
// eslint-disable-next-line @typescript-eslint/no-namespace
namespace Utf8 {
  export function encode(strUni: string): string {
    // use regular expressions & String.replace callback function for better efficiency
    // than procedural approaches
    let strUtf = strUni.replace(
      /[\u0080-\u07ff]/g, // U+0080 - U+07FF => 2 bytes 110yyyyy, 10zzzzzz

      function (c) {
        const cc = c.charCodeAt(0)
        return String.fromCharCode(0xc0 | (cc >> 6), 0x80 | (cc & 0x3f))
      },
    )
    strUtf = strUtf.replace(
      /[\u0800-\uffff]/g, // U+0800 - U+FFFF => 3 bytes 1110xxxx, 10yyyyyy, 10zzzzzz

      function (c) {
        const cc = c.charCodeAt(0)
        return String.fromCharCode(0xe0 | (cc >> 12), 0x80 | ((cc >> 6) & 0x3f), 0x80 | (cc & 0x3f))
      },
    )
    return strUtf
  }
  /**
   * Decode utf-8 encoded string back into multi-byte Unicode characters
   *
   * @param {String} strUtf UTF-8 string to be decoded back to Unicode
   * @returns {String} decoded string
   */
  export function decode(strUtf: string): string {
    // note: decode 3-byte chars first as decoded 2-byte strings could appear to be 3-byte char!
    let strUni = strUtf.replace(
      /[\u00e0-\u00ef][\u0080-\u00bf][\u0080-\u00bf]/g, // 3-byte chars

      function (c) {
        // (note parentheses for precence)
        const cc =
          ((c.charCodeAt(0) & 0x0f) << 12) |
          ((c.charCodeAt(1) & 0x3f) << 6) |
          (c.charCodeAt(2) & 0x3f)
        return String.fromCharCode(cc)
      },
    )
    strUni = strUni.replace(
      /[\u00c0-\u00df][\u0080-\u00bf]/g, // 2-byte chars

      function (c) {
        // (note parentheses for precence)
        const cc = ((c.charCodeAt(0) & 0x1f) << 6) | (c.charCodeAt(1) & 0x3f)
        return String.fromCharCode(cc)
      },
    )
    return strUni
  }
}

const encrypt = (plaintext: string, password: string): string => {
  if (plaintext.length === 0) return '' // nothing to encrypt
  // convert string to array of longs after converting any multi-byte chars to UTF-8
  const v = strToLongs(Utf8.encode(plaintext))
  if (v.length <= 1) v[1] = 0 // algorithm doesn't work for n<2 so fudge by adding a null
  // simply convert first 16 chars of password as key
  const k = strToLongs(Utf8.encode(password).slice(0, 16))
  const n = v.length

  let z = v[n - 1]
  let y = v[0]
  const delta = 0x9e3779b9
  let mx: number
  let e: number
  let q = Math.floor(6 + 52 / n)
  let sum = 0

  while (q > 0) {
    q -= 1
    // 6 + 52/n operations gives between 6 & 32 mixes on each word
    sum += delta
    e = (sum >>> 2) & 3
    for (let p = 0; p < n; p++) {
      y = v[(p + 1) % n]
      mx = (((z >>> 5) ^ (y << 2)) + ((y >>> 3) ^ (z << 4))) ^ ((sum ^ y) + (k[(p & 3) ^ e] ^ z))
      z = v[p] += mx
    }
  }

  const ciphertext = longsToStr(v)

  return Base64.encode(ciphertext)
}

const decrypt = (ciphertext: string, password: string): string => {
  if (ciphertext.length === 0) return ''
  const v = strToLongs(Base64.decode(ciphertext))
  const k = strToLongs(Utf8.encode(password).slice(0, 16))
  const n = v.length

  let z = v[n - 1]
  let y = v[0]
  const delta = 0x9e3779b9
  let mx: number
  let e: number
  const q = Math.floor(6 + 52 / n)
  let sum = q * delta

  while (sum !== 0) {
    e = (sum >>> 2) & 3
    for (let p = n - 1; p >= 0; p--) {
      z = v[p > 0 ? p - 1 : n - 1]
      mx = (((z >>> 5) ^ (y << 2)) + ((y >>> 3) ^ (z << 4))) ^ ((sum ^ y) + (k[(p & 3) ^ e] ^ z))
      y = v[p] -= mx
    }
    sum -= delta
  }

  let plaintext = longsToStr(v)

  // strip trailing null chars resulting from filling 4-char blocks:
  plaintext = plaintext.replace(/\0+$/, '')

  return Utf8.decode(plaintext)
}

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

// supporting functions
function strToLongs(s: string): number[] {
  // convert string to array of longs, each containing 4 chars
  // note chars must be within ISO-8859-1 (with Unicode code-point < 256) to fit 4/long
  const l = new Array<number>(Math.ceil(s.length / 4))
  for (let i = 0; i < l.length; i++) {
    // note little-endian encoding - endianness is irrelevant as long as
    // it is the same in longsToStr()
    l[i] =
      s.charCodeAt(i * 4) +
      (s.charCodeAt(i * 4 + 1) << 8) +
      (s.charCodeAt(i * 4 + 2) << 16) +
      (s.charCodeAt(i * 4 + 3) << 24)
  }
  return l // note running off the end of the string generates nulls since
} // bitwise operators treat NaN as 0

function longsToStr(l: number[]): string {
  // convert array of longs back to string
  const a = new Array(l.length)
  for (let i = 0; i < l.length; i++) {
    a[i] = String.fromCharCode(
      l[i] & 0xff,
      (l[i] >>> 8) & 0xff,
      (l[i] >>> 16) & 0xff,
      (l[i] >>> 24) & 0xff,
    )
  }
  return a.join('') // use Array.join() rather than repeated string appends for efficiency in IE
}

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace Base64 {
  export const code = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='
  /**
   * Encode string into Base64, as defined by RFC 4648 [http://tools.ietf.org/html/rfc4648]
   * (instance method extending String object). As per RFC 4648, no newlines are added.
   *
   * @param {String} str The string to be encoded as base-64
   * @param {Boolean} [utf8encode=false] Flag to indicate whether str is Unicode string to be encoded
   *   to UTF8 before conversion to base64; otherwise string is assumed to be 8-bit characters
   * @returns {String} Base64-encoded string
   */
  export function encode(str: string, utf8encode?: boolean): string {
    // http://tools.ietf.org/html/rfc4648
    const shouldUtf8Encode = utf8encode ?? false
    let o1: number,
      o2: number,
      o3: number,
      bits: number,
      h1: number,
      h2: number,
      h3: number,
      h4: number
    const e: string[] = []
    let pad = ''
    let c: number
    let plain: string
    let coded: string
    const b64 = Base64.code

    plain = shouldUtf8Encode ? Utf8.encode(str) : str

    c = plain.length % 3 // pad string to length of multiple of 3
    if (c > 0) {
      while (c++ < 3) {
        pad += '='
        plain += '\0'
      }
    }
    // note: doing padding here saves us doing special-case packing for trailing 1 or 2 chars
    for (c = 0; c < plain.length; c += 3) {
      // pack three octets into four hexets
      o1 = plain.charCodeAt(c)
      o2 = plain.charCodeAt(c + 1)
      o3 = plain.charCodeAt(c + 2)

      bits = (o1 << 16) | (o2 << 8) | o3

      h1 = (bits >> 18) & 0x3f
      h2 = (bits >> 12) & 0x3f
      h3 = (bits >> 6) & 0x3f
      h4 = bits & 0x3f

      // use hextets to index into code string
      e[c / 3] = b64.charAt(h1) + b64.charAt(h2) + b64.charAt(h3) + b64.charAt(h4)
    }
    coded = e.join('') // join() is far faster than repeated string concatenation in IE
    // replace 'A's from padded nulls with '='s
    coded = coded.slice(0, coded.length - pad.length) + pad

    return coded
  }

  /**
   * Decode string from Base64, as defined by RFC 4648 [http://tools.ietf.org/html/rfc4648]
   * (instance method extending String object). As per RFC 4648, newlines are not catered for.
   *
   * @param {String} str The string to be decoded from base-64
   * @param {Boolean} [utf8decode=false] Flag to indicate whether str is Unicode string to be decoded
   *   from UTF8 after conversion from base64
   * @returns {String} decoded string
   */
  export function decode(str: string, utf8decode?: boolean): string {
    const shouldUtf8Decode = utf8decode ?? false
    let o1: number,
      o2: number,
      o3: number,
      h1: number,
      h2: number,
      h3: number,
      h4: number,
      bits: number
    const d: string[] = []
    const b64 = Base64.code

    const coded = shouldUtf8Decode ? Utf8.decode(str) : str

    for (let c = 0; c < coded.length; c += 4) {
      // unpack four hexets into three octets
      h1 = b64.indexOf(coded.charAt(c))
      h2 = b64.indexOf(coded.charAt(c + 1))
      h3 = b64.indexOf(coded.charAt(c + 2))
      h4 = b64.indexOf(coded.charAt(c + 3))

      bits = (h1 << 18) | (h2 << 12) | (h3 << 6) | h4

      o1 = (bits >>> 16) & 0xff
      o2 = (bits >>> 8) & 0xff
      o3 = bits & 0xff

      d[c / 4] = String.fromCharCode(o1, o2, o3)
      // check for padding
      if (h4 === 0x40) d[c / 4] = String.fromCharCode(o1, o2)
      if (h3 === 0x40) d[c / 4] = String.fromCharCode(o1)
    }
    const joinedPlain = d.join('') // join() is far faster than repeated string concatenation in IE
    return shouldUtf8Decode ? Utf8.decode(joinedPlain) : joinedPlain
  }
}

export function decryptData<T>(data: string, decrpytKey: string): T {
  return ParseJson(decrypt(data, decrpytKey)) as T
}

export const encryptData = (data: unknown, encryptKey: string): string => {
  let input: unknown = data === undefined ? null : data
  if (typeof input === 'object') {
    input = JSON.stringify(input)
  }

  return encrypt(`${input}`, encryptKey)
}

const ParseJson = (value: string): unknown => {
  switch (typeof value) {
    case 'string': // JSON 안되기도 함
      try {
        return JSON.parse(value)
      } catch {
        // SyntaxError:Unexpected end of JSON input
        // SyntaxError:Unexpected token 'q', "q2" is not valid JSON
        return value
      }
    case 'object':
    case 'undefined': // 안됨
      return value
    case 'boolean': //됨
    case 'number': // 됨
      return JSON.parse(JSON.stringify(value))
  }
}
